import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Field, FormError, SubmitButton } from "@/components/common";
import { VehicleFields, EMPTY_VEHICLE, vehiclePayload } from "@/components/pro/VehicleForm";
import { api, errMsg, errDetail } from "@/lib/api";
import { t } from "@/i18n";

export function DuplicateWarning({ matches, code, onConfirm, busy }) {
  if (!matches?.length) return null;
  return (
    <div data-testid="duplicate-warning" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
      <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> {t("customers.dupTitle")}</p>
      <ul className="mt-3 space-y-2">
        {matches.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white/70 p-2" data-testid={`duplicate-match-${m.id}`}>
            <span><strong>{m.name}</strong> · {m.email || m.phone || "—"} <span className="text-xs text-amber-800">({t("customers.matchedOn")} {m.matched_on.map((x) => t(`customers.match.${x}`)).join(", ")})</span></span>
            <Link to={`/pro/customers/${m.id}`} className="text-xs font-semibold text-teal-800 underline" data-testid={`open-duplicate-${m.id}`}>{t("customers.openRecord")}</Link>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs">{t("customers.noAutoMerge")}</p>
      {code === "possible_duplicate" && onConfirm && (
        <Button type="button" size="sm" variant="outline" className="mt-3" disabled={busy} onClick={onConfirm} data-testid="create-anyway-button">{t("customers.createAnyway")}</Button>
      )}
    </div>
  );
}

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", address: "", notes: "", comm_prefs: { email: true, sms: false } };

export function CustomerForm({ customer, onSaved }) {
  const editing = !!customer;
  const [f, setF] = useState(customer ? { ...EMPTY, ...customer, email: customer.email || "" } : EMPTY);
  const [withVehicle, setWithVehicle] = useState(false);
  const [v, setV] = useState(EMPTY_VEHICLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const precheck = async () => {
    if (editing || !(f.email || f.phone || (withVehicle && v.plate))) return;
    try {
      const { data } = await api.post("/customers/check-duplicates", { email: f.email || null, phone: f.phone || null, plate: withVehicle ? v.plate : null });
      setDup(data.matches.length ? { matches: data.matches, code: "precheck" } : null);
    } catch { /* non-blocking */ }
  };

  const submit = async (confirm = false) => {
    setBusy(true);
    setError("");
    const payload = { first_name: f.first_name, last_name: f.last_name, email: f.email || null, phone: f.phone, address: f.address, notes: f.notes, comm_prefs: f.comm_prefs };
    try {
      const { data } = editing
        ? await api.put(`/customers/${customer.id}`, payload)
        : await api.post("/customers", { ...payload, confirm_duplicate: confirm, vehicle: withVehicle ? vehiclePayload(v) : null });
      onSaved(data);
    } catch (e) {
      const d = errDetail(e);
      if (d?.matches) setDup({ matches: d.matches, code: d.code });
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-4" data-testid="customer-form">
      <FormError message={error} />
      <DuplicateWarning matches={dup?.matches} code={dup?.code} busy={busy} onConfirm={() => submit(true)} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("customers.firstName")} htmlFor="cf-first"><Input id="cf-first" required value={f.first_name} onChange={set("first_name")} data-testid="customer-first-name-input" /></Field>
        <Field label={t("customers.lastName")} htmlFor="cf-last"><Input id="cf-last" value={f.last_name} onChange={set("last_name")} data-testid="customer-last-name-input" /></Field>
        <Field label={t("auth.email")} htmlFor="cf-email" hint={customer?.has_portal_account ? t("customers.emailLocked") : null}>
          <Input id="cf-email" type="email" value={f.email} onChange={set("email")} onBlur={precheck} disabled={customer?.has_portal_account} data-testid="customer-email-input" />
        </Field>
        <Field label={t("customers.phone")} htmlFor="cf-phone"><Input id="cf-phone" value={f.phone} onChange={set("phone")} onBlur={precheck} data-testid="customer-phone-input" /></Field>
        <Field label={t("customers.address")} htmlFor="cf-addr" className="sm:col-span-2"><Input id="cf-addr" value={f.address} onChange={set("address")} data-testid="customer-address-input" /></Field>
      </div>
      <div className="flex flex-wrap gap-6 rounded-lg border border-slate-200 p-3 text-sm">
        <span className="font-medium text-slate-800">{t("customers.commPrefs")}</span>
        {["email", "sms", "service_reminders", "marketing"].map((k) => (
          <label key={k} className="flex items-center gap-2">
            <Switch checked={f.comm_prefs[k] ?? k === "service_reminders"} onCheckedChange={(val) => setF({ ...f, comm_prefs: { ...f.comm_prefs, [k]: val } })} data-testid={`customer-comm-${k}-switch`} />
            {t(`customers.comm.${k}`)}
          </label>
        ))}
      </div>
      <Field label={t("customers.notes")} htmlFor="cf-notes"><Textarea id="cf-notes" value={f.notes} onChange={set("notes")} data-testid="customer-notes-input" /></Field>
      {!editing && (
        <div className="rounded-lg border border-slate-200 p-4">
          <label className="flex items-center justify-between text-sm font-medium text-slate-800">
            {t("customers.addVehicleNow")}
            <Switch checked={withVehicle} onCheckedChange={setWithVehicle} data-testid="customer-with-vehicle-switch" />
          </label>
          {withVehicle && <div className="mt-4"><VehicleFields v={v} setV={setV} onPlateBlur={precheck} /></div>}
        </div>
      )}
      <div className="gf-modal-footer">
      <SubmitButton busy={busy} className="w-full" data-testid="customer-save-button">{editing ? t("common.save") : t("customers.create")}</SubmitButton>
      </div>
    </form>
  );
}
